// Google Analytics utility functions - Enhanced version
interface WindowWithGA extends Window {
  gtag?: (...args: any[]) => void;
  dataLayer?: any[];
}

declare const window: WindowWithGA;

// Your Google Analytics Measurement ID
const GA_MEASUREMENT_ID = "G-CT7H4EQ8GJ";

// User properties - useful for segmentation
export const UserProperties = {
  USER_TYPE: "user_type",
  SUBSCRIPTION_STATUS: "subscription_status",
  ACCOUNT_AGE: "account_age",
  USER_SEGMENT: "user_segment",
  DEVICE_TYPE: "device_type",
  REFERRAL_SOURCE: "referral_source",
};

// Custom event names - standardize across the application
export const EventNames = {
  // Navigation events
  PAGE_VIEW: "page_view",
  SCROLL_DEPTH: "scroll_depth",
  NAVIGATION_CLICK: "navigation_click",

  // Engagement events
  BUTTON_CLICK: "button_click",
  LINK_CLICK: "link_click",
  FORM_START: "form_start",
  FORM_COMPLETION: "form_completion",
  FORM_ABANDONMENT: "form_abandonment",
  VIDEO_PLAY: "video_play",
  VIDEO_PROGRESS: "video_progress",
  VIDEO_COMPLETE: "video_complete",
  FILE_DOWNLOAD: "file_download",

  // Conversion events
  SIGN_UP_START: "sign_up_start",
  SIGN_UP_COMPLETE: "sign_up_complete",
  PILOT_TEST_REQUEST: "pilot_test_request",
  CONTACT_FORM_SUBMIT: "contact_form_submit",
  SCHEDULE_DEMO: "schedule_demo",

  // UX/UI interaction events
  TOOLTIP_CLICK: "tooltip_click",
  MODAL_OPEN: "modal_open",
  MODAL_CLOSE: "modal_close",
  FILTER_SELECTION: "filter_selection",
  SEARCH_QUERY: "search_query",

  // Error events
  FORM_ERROR: "form_error",
  PAGE_ERROR: "page_error",
  API_ERROR: "api_error",

  // Timing events
  TIME_ON_PAGE: "time_on_page",
};

// Custom dimensions
export const CustomDimensions = {
  PAGE_TEMPLATE: "page_template",
  USER_ENGAGEMENT_SCORE: "user_engagement_score",
  SESSION_ENGAGEMENT: "session_engagement",
  SCROLL_PERCENTAGE: "scroll_percentage",
  TIME_ON_PAGE: "time_on_page",
};

// Page timing variables
let pageLoadTime: Date | null = null;
let lastPagePath: string = "";
let isScrollThrottled = false;
let trackedScrollDepths: number[] = [];

// Initialize GA with advanced configuration
export const initGA = (measurementId: string = GA_MEASUREMENT_ID) => {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      // Basic configuration
      window.gtag("config", measurementId, {
        send_page_view: false, // We'll send page views manually for SPA
        anonymize_ip: true, // Privacy enhancement
        cookie_flags: "secure;samesite=none", // Enhanced cookie security
        cookie_update: true,
        custom_map: {
          // Map custom dimensions
          dimension1: CustomDimensions.PAGE_TEMPLATE,
          dimension2: CustomDimensions.USER_ENGAGEMENT_SCORE,
          dimension3: CustomDimensions.SESSION_ENGAGEMENT,
          dimension4: CustomDimensions.SCROLL_PERCENTAGE,
          dimension5: CustomDimensions.TIME_ON_PAGE,
        },
      });

      // Set default session parameters
      setUserProperties({
        [UserProperties.DEVICE_TYPE]: detectDeviceType(),
        [UserProperties.REFERRAL_SOURCE]: detectReferralSource(),
      });

      // Configure consent mode
      configureConsentMode();

      console.log("Google Analytics initialized successfully");
    }
  } catch (error) {
    console.error("Error initializing Google Analytics:", error);
  }
};

// Enhanced page view tracking with timing and template type
export const trackPageView = (
  url: string,
  pageTitle: string = document.title,
  pageTemplate: string = "standard"
) => {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      // Record page view with enhanced parameters
      window.gtag("event", "page_view", {
        page_title: pageTitle,
        page_location: window.location.href,
        page_path: url,
        send_to: GA_MEASUREMENT_ID,
        [CustomDimensions.PAGE_TEMPLATE]: pageTemplate,
      });

      // Start timing for time on page measurement
      startPageViewTiming(url);

      // Setup scroll depth tracking for this page
      setupScrollTracking();

      // Reset scroll tracking for the new page
      trackedScrollDepths = [];
    }
  } catch (error) {
    console.error("Error tracking page view:", error);
  }
};

// Enhanced event tracking
export const trackEvent = (
  eventName: string,
  eventParams: Record<string, any> = {}
) => {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", eventName, {
        ...eventParams,
        send_to: GA_MEASUREMENT_ID,
        timestamp: new Date().toISOString(),
        page_title: document.title,
        page_location: window.location.href,
        page_path: window.location.pathname,
      });
    }
  } catch (error) {
    console.error(`Error tracking event ${eventName}:`, error);
  }
};

// Track conversion events (more important actions)
export const trackConversion = (
  eventName: string,
  eventParams: Record<string, any> = {}
) => {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      // Track as both a standard event and a conversion
      window.gtag("event", eventName, {
        ...eventParams,
        send_to: GA_MEASUREMENT_ID,
        value: eventParams.value || 1, // Default value for conversions
        non_interaction: false, // This is an interactive event
      });
    }
  } catch (error) {
    console.error(`Error tracking conversion ${eventName}:`, error);
  }
};

// Set user properties for better segmentation
export const setUserProperties = (properties: Record<string, any>) => {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("set", "user_properties", properties);
    }
  } catch (error) {
    console.error("Error setting user properties:", error);
  }
};

// Track exceptions and errors
export const trackException = (description: string, fatal: boolean = false) => {
  try {
    if (typeof window !== "undefined" && window.gtag) {
      window.gtag("event", "exception", {
        description,
        fatal,
      });
    }
  } catch (error) {
    console.error("Error tracking exception:", error);
  }
};

// Track form interactions (start, completion, errors)
export const trackFormInteraction = (
  formName: string,
  action: "start" | "complete" | "error" | "abandon",
  formData?: Record<string, any>
) => {
  try {
    const eventName =
      action === "start"
        ? EventNames.FORM_START
        : action === "complete"
        ? EventNames.FORM_COMPLETION
        : action === "error"
        ? EventNames.FORM_ERROR
        : EventNames.FORM_ABANDONMENT;

    trackEvent(eventName, {
      form_name: formName,
      form_action: action,
      ...formData,
    });
  } catch (error) {
    console.error(`Error tracking form interaction ${action}:`, error);
  }
};

// Helper to detect device type
const detectDeviceType = (): string => {
  const ua = navigator.userAgent;
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) {
    return "tablet";
  }
  if (
    /Mobile|Android|iP(hone|od)|IEMobile|BlackBerry|Kindle|Silk-Accelerated|(hpw|web)OS|Opera M(obi|ini)/.test(
      ua
    )
  ) {
    return "mobile";
  }
  return "desktop";
};

// Helper to detect referral source
const detectReferralSource = (): string => {
  if (document.referrer) {
    try {
      const referrer = new URL(document.referrer);
      return referrer.hostname;
    } catch (e) {
      return "invalid-referrer";
    }
  }
  return "direct";
};

// Start timing for a page view
const startPageViewTiming = (pagePath: string): void => {
  pageLoadTime = new Date();
  lastPagePath = pagePath;

  // Set up a listener for when the user leaves the page
  window.addEventListener("beforeunload", recordTimeOnPage);
};

// Record time spent on page when leaving
const recordTimeOnPage = (): void => {
  if (pageLoadTime && lastPagePath) {
    const timeOnPageMs = new Date().getTime() - pageLoadTime.getTime();
    const timeOnPageSec = Math.round(timeOnPageMs / 1000);

    // Don't send if they bounced too quickly
    if (timeOnPageSec > 1) {
      trackEvent(EventNames.TIME_ON_PAGE, {
        [CustomDimensions.TIME_ON_PAGE]: timeOnPageSec,
        page_path: lastPagePath,
        non_interaction: true,
      });
    }
  }
};

// Calculate scroll percentage
const calculateScrollPercentage = (): number => {
  const scrollTop = window.pageYOffset;
  const windowHeight = window.innerHeight;
  const docHeight = getDocumentHeight();
  const trackLength = docHeight - windowHeight;
  return trackLength > 0 ? Math.floor((scrollTop / trackLength) * 100) : 100;
};

// Get document height
const getDocumentHeight = (): number => {
  return Math.max(
    document.body.scrollHeight,
    document.documentElement.scrollHeight,
    document.body.offsetHeight,
    document.documentElement.offsetHeight,
    document.body.clientHeight,
    document.documentElement.clientHeight
  );
};

// Process scroll events
const onScroll = (): void => {
  if (isScrollThrottled) return;

  isScrollThrottled = true;
  setTimeout(() => {
    isScrollThrottled = false;
  }, 250);

  const scrollPercentage = calculateScrollPercentage();
  const scrollDepths = [25, 50, 75, 90, 100];

  // Check which scroll depths have been passed
  for (const depth of scrollDepths) {
    if (scrollPercentage >= depth && !trackedScrollDepths.includes(depth)) {
      trackedScrollDepths.push(depth);

      trackEvent(EventNames.SCROLL_DEPTH, {
        [CustomDimensions.SCROLL_PERCENTAGE]: depth,
        page_path: window.location.pathname,
        non_interaction: true,
      });
    }
  }
};

// Configure scroll depth tracking
const setupScrollTracking = (): void => {
  // Remove any existing listener first to prevent duplicates
  window.removeEventListener("scroll", onScroll);

  // Add the scroll event listener
  window.addEventListener("scroll", onScroll);
};

// Configure consent mode (if needed)
const configureConsentMode = (): void => {
  if (typeof window !== "undefined" && window.gtag) {
    window.gtag("consent", "default", {
      ad_storage: "denied",
      analytics_storage: "granted",
      wait_for_update: 500,
    });
  }
};

// Outbound link tracking
export const trackOutboundLink = (
  url: string,
  linkText: string = "",
  destination: string = "external"
): void => {
  trackEvent(EventNames.LINK_CLICK, {
    link_url: url,
    link_text: linkText,
    link_destination: destination,
  });
};

// Button click tracking
export const trackButtonClick = (
  buttonName: string,
  buttonLocation: string = window.location.pathname
): void => {
  trackEvent(EventNames.BUTTON_CLICK, {
    button_name: buttonName,
    button_location: buttonLocation,
  });
};

// Track video interactions
export const trackVideoInteraction = (
  videoName: string,
  action: "play" | "progress" | "complete",
  progress?: number
): void => {
  const eventName =
    action === "play"
      ? EventNames.VIDEO_PLAY
      : action === "complete"
      ? EventNames.VIDEO_COMPLETE
      : EventNames.VIDEO_PROGRESS;

  trackEvent(eventName, {
    video_name: videoName,
    video_action: action,
    video_progress: progress,
  });
};

// Track file downloads
export const trackDownload = (
  fileName: string,
  fileType: string,
  fileSize?: number
): void => {
  trackEvent(EventNames.FILE_DOWNLOAD, {
    file_name: fileName,
    file_type: fileType,
    file_size: fileSize,
  });
};

// Track search queries
export const trackSearch = (query: string, resultsCount: number): void => {
  trackEvent(EventNames.SEARCH_QUERY, {
    search_term: query,
    search_results_count: resultsCount,
  });
};

// Track any errors that occur
export const trackError = (
  errorType: string,
  errorMessage: string,
  errorSource: string
): void => {
  trackEvent(EventNames.PAGE_ERROR, {
    error_type: errorType,
    error_message: errorMessage,
    error_source: errorSource,
  });
};
